import ColorThief from 'colorthief';

/**
 * @public — the name itself isn't referenced anywhere outside this file (only structurally, via
 * `extractPaletteFromImage`'s inferred return type), so knip otherwise flags it as unused.
 */
export interface BrandPalette {
  primary: string;
  secondary: string;
  accent: string;
  darkText: string;
  lightBackground: string;
  extractedColors: string[];
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
}

/**
 * Extracts a suggested brand palette from an image file
 */
export async function extractPaletteFromImage(fileOrUrl: string | File): Promise<BrandPalette> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    let objectUrl: string | null = null;
    img.onload = () => {
      try {
        // @ts-expect-error - ported unchanged from the standalone tool; colorthief's shipped
        // types describe the Node build, not the browser class this constructs
        const colorThief = new ColorThief();
        const dominantColor = colorThief.getColor(img);
        const palette = colorThief.getPalette(img, 8);

        // Very basic mapping for the MVP
        resolve({
          primary: rgbToHex(dominantColor[0], dominantColor[1], dominantColor[2]),
          secondary:
            palette.length > 1 ? rgbToHex(palette[1][0], palette[1][1], palette[1][2]) : '#555555',
          accent:
            palette.length > 2 ? rgbToHex(palette[2][0], palette[2][1], palette[2][2]) : '#999999',
          darkText: '#111111',
          lightBackground: '#ffffff',
          extractedColors: [
            rgbToHex(dominantColor[0], dominantColor[1], dominantColor[2]),
            ...palette.map((p: number[]) => rgbToHex(p[0], p[1], p[2])),
          ],
        });
      } catch (e) {
        reject(e);
      } finally {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      }
    };
    img.onerror = e => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      reject(e);
    };

    if (fileOrUrl instanceof File) {
      objectUrl = URL.createObjectURL(fileOrUrl);
      img.src = objectUrl;
    } else {
      img.src = fileOrUrl;
    }
  });
}
